import { useEffect } from 'react'
import { useMagnum } from '@/context/MagnumContext'
import { cn } from '@/lib/utils'
import { timArt, preloadTimArt } from '@/lib/timArt'

/**
 * The one Tim. He stands at the top of the kid's shell for the whole session and
 * says whatever the screen needs said — engine reactions, and any message a page
 * hands up through MagnumSays. Nothing else on a kid screen draws his face.
 */
export default function MagnumCompanion() {
  const { quip, isFrameIdle, announcement, registerCompanion, isTakenOver } = useMagnum()

  // Every pose is swapped in from this one component, so warm them all once.
  useEffect(() => { preloadTimArt() }, [])

  // Tells MagnumSays instances there is a Tim here to speak for them.
  useEffect(() => registerCompanion(), [registerCompanion])

  // A page's own message describes the state the kid is looking at, so it wins
  // over the last thing the reaction engine happened to say.
  const body = announcement ? announcement.node : quip?.text ?? null
  const variant = announcement?.variant ?? 'default'

  const bubbleTone = isFrameIdle
    ? 'border-primary bg-primary/5'
    : variant === 'warning'
      ? 'border-red-300 bg-red-50'
      : variant === 'success'
        ? 'border-green-300 bg-green-50'
        : 'border-border bg-card'

  const tailTone = isFrameIdle
    ? 'border-r-primary'
    : variant === 'warning'
      ? 'border-r-red-300'
      : variant === 'success'
        ? 'border-r-green-300'
        : 'border-r-border'

  const tailFill = isFrameIdle
    ? 'border-r-primary/5'
    : variant === 'warning'
      ? 'border-r-red-50'
      : variant === 'success'
        ? 'border-r-green-50'
        : 'border-r-card'

  // Announcements are page state, not a reaction, so they don't drive his mood.
  const pose = announcement
    ? (variant === 'success' ? 'happy' : variant === 'warning' ? 'encouraging' : 'neutral')
    : quip?.mood

  // A full-screen ceremony is showing its own Tim; two would be one too many.
  if (isTakenOver) return null

  return (
    <div className="flex items-end gap-1 min-h-[80px]">
      <img
        src={timArt(pose)}
        alt="Tim"
        className={cn(
          'w-16 h-16 sm:w-20 sm:h-20 object-contain shrink-0 transition-all duration-500',
          isFrameIdle ? 'magnum-character-idle w-20 h-20 sm:w-24 sm:h-24' : 'magnum-character',
        )}
      />
      {body && (
        <div className="relative animate-in fade-in slide-in-from-left-2 duration-300">
          <div className={cn(
            'relative border-2 rounded-2xl rounded-bl-none px-4 py-2.5 max-w-sm shadow-sm transition-colors duration-500',
            bubbleTone,
          )}>
            <div className="text-sm sm:text-base font-medium">{body}</div>
          </div>
          {/* Speech bubble tail */}
          <div className={cn(
            'absolute bottom-0 -left-[8px] w-0 h-0 border-t-[8px] border-r-[8px] border-t-transparent transition-colors duration-500',
            tailTone,
          )} />
          <div className={cn(
            'absolute bottom-[2px] -left-[4px] w-0 h-0 border-t-[6px] border-r-[6px] border-t-transparent transition-colors duration-500',
            tailFill,
          )} />
        </div>
      )}
    </div>
  )
}
